<template>
  <div :id="id" class="results-map"></div>
</template>

<script>
import { mapGetters } from "vuex";
import d3 from '@/plugins/d3';
import * as topojson from "topojson";

import { ConfigMixin, MapMixin, TableMixin } from "@/mixins";

const props = {
  id: {
    type: String,
    required: true
  },
  results: {
    type: Array,
    required: true
  },
  geoSelection: {
    type: String,
    required: false
  },
  geoType: {
    type: String,
    required: true
  }
};

const mixins = [ConfigMixin, MapMixin, TableMixin];

const computed = {
  ...mapGetters({
    cities: "geojson/cities",
    constituencies: "geojson/constituencies",
    states: "geojson/states"
  }),
  tooltipId() {
    return `${this.id}-tooltip`;
  },
  tooltipContentId() {
    return `${this.tooltipId}-content`;
  }
};

const methods = {
  setGeoSelection(value) {
    this.$emit("update", value);
  },
  getSelectedResults(d) {
    return this.results.find(item => {
      return item.seat_name === d.properties.WKR_NAME;
    });
  },
  getTopojson(geographies, match) {
    let geojson = JSON.parse(geographies);
    return topojson.feature(geojson, geojson.objects[match]);
  },
  drawCities() {
    const self = this;

    let topojson = self.getTopojson(self.cities, "cities");

    self.gCities = self.svg.append("g");

    // self.gCities
    //   .append("path")
    //   .datum(topojson)
    //   .attr("d", self.path)
    //   .attr("class", "cities");

    self.gCities
      .selectAll("circle")
      .data(topojson.features)
      .enter()
      .append("circle")
      .attr("r", 2)
      .attr("transform", function(d) {
        return (
          "translate(" +
          self.projection([d.properties.LONGITUDE, d.properties.LATITUDE]) +
          ")"
        );
      });

    self.gCities
      .selectAll(".cities")
      .data(topojson.features)
      .enter()
      .append("text")
      .attr("class", "city-label shadow");

    self.gCities
      .selectAll(".cities")
      .data(topojson.features)
      .enter()
      .append("text")
      .attr("class", "city-label");

    self.gCities
      .selectAll(".city-label")
      .attr("transform", function(d) {
        return "translate(" + self.projection(d.geometry.coordinates) + ")";
      })
      .attr("dy", ".25em")
      .text(function(d) {
        return d.properties.CITY_NAME;
      })
      .attr("x", 6)
      .attr("y", 4)
      .attr("font-size", "12px");
  },
  drawOverlay(geojson, match) {
    const self = this;

    const topojson = self.getTopojson(geojson, match);

    self.gOverlay = self.svg.append("g");

    self.gOverlay
      .selectAll("path")
      .data(topojson.features)
      .enter()
      .append("path")
      .attr("class", "states")
      .attr("stroke", "#222")
      .attr("d", self.path)
      .attr("pointer-events", "none")
      .style("fill", "rgba(0,0,0,0)")
      .style("stroke-width", 1);

    self.gLabels = self.svg.append("g");

    // self.gLabels
    //   .selectAll(".states")
    //   .data(topojson.features)
    //   .enter()
    //   .append("text")
    //   .attr("class", "country-labels")
    //   .attr("transform", function(d) {
    //     return "translate(" + self.path.centroid(d) + ")";
    //   })
    //   .attr("dy", "-1em")
    //   .style("opacity", 1)
    //   .text(function(d) {
    //     if (d.properties.ctry18nm === "Northern Ireland") {
    //       return "N. Ireland";
    //     }
    //     return d.properties.ctry18nm;
    //   });
  },
  drawMap() {
    const self = this;

    const mobile = window.innerWidth < 750

    let topojson = this.getTopojson(self[this.geoType], this.geoType);

    d3.selection.prototype.moveToFront = function() {
      return this.each(function() {
        this.parentNode.appendChild(this);
      });
    };

    self.clearTooltip();
    const map = d3.select(`#${self.id}`);
    const svgId = `${self.id}-svg`;
    const prevSvg = document.getElementById(svgId);
    if (prevSvg) {
      prevSvg.parentNode.removeChild(prevSvg);
    }

    map.select(`#${svgId}`).remove();

    const container = map.node();
    let width = container.clientWidth;
    let height = container.clientHeight;
    self.active = d3.select(null);

    self.projection = d3.geoMercator()
      // .center([0, 55.4])
      // .rotate([4.4, 0])
      // .parallels([50, 60])
      .scale(6000)
      .translate([width / 2, height / 2])
      .fitSize([width, height - 30], topojson);

    // setting up zoom functionality
    self.zoom = d3
      .zoom()
      .scaleExtent([1, 8])
      .on("zoom", zoomed);

    function zoomTo(d) {
      if (self.active.node() === self) return self.resetZoom();
      self.active.classed("active", false);
      self.active = d3.select(this).classed("active", true);

      const bounds = self.path.bounds(d);
      const dx = bounds[1][0] - bounds[0][0];
      const dy = bounds[1][1] - bounds[0][1];
      const x = (bounds[0][0] + bounds[1][0]) / 2;
      const y = (bounds[0][1] + bounds[1][1]) / 2;
      const scale = Math.max(
        1,
        Math.min(8, 0.9 / Math.max(dx / width, dy / height))
      );
      const translate = [width / 2 - scale * x, height / 2 - scale * y];

      self.svg
        .transition()
        .duration(750)
        .call(
          self.zoom.transform,
          d3.zoomIdentity.translate(translate[0], translate[1]).scale(scale)
        );

      if (self.gLabels) {
        self.gLabels.selectAll("text").style("opacity", 0);
      }

      if (self.gCities) {
        self.gCities.selectAll("text").style("opacity", 0.5);
        self.gCities.selectAll("circle").style("opacity", 0.5);
      }

      if (self.gOverlay) {
        self.gOverlay.selectAll("path").style("opacity", 0);
      }
    }

    function zoomed(event) {
      if (!mobile) {
        self.tooltip.style("display", "none");
      }
      self.g.attr("transform", event.transform);
      self.gCities.attr("transform", event.transform);
      // self.gCities.selectAll(".city-label").style("font-size", "6px");
    }

    function stopped(event) {
      if (event.defaultPrevented) event.stopPropagation();
    }

    self.path = d3.geoPath().projection(self.projection);

    self.svg = d3
      .select(`#${self.id}`)
      .append("svg")
      .attr("width", width)
      .attr("height", height)
      .attr("id", svgId)
      .on("click", stopped, true)
      .on("click", (event) => {
          // this.resetZoom();
          if (!mobile) {
            self.tooltip.style("display", "none");
          }
          if (event.defaultPrevented) {
            event.stopPropagation();
          }
          self.setGeoSelection(null);
        },
        true
      );

    self.g = self.svg.append("g");

    // svg.call(zoom);

    self.initializeToolTip();

    function tooltipPos (event) {
      if (!mobile) {
        const height = self.tooltip._groups[0][0].clientHeight + 12
        const width = self.tooltip._groups[0][0].clientWidth + 12
        const screenHeight = document.documentElement.clientHeight
        const screenWidth = document.documentElement.clientWidth
        self.tooltip
          .style('left', function () {
            if (event.pageX + width + 10 < screenWidth) {
              return (event.pageX + 20) + 'px'
            } else {
              return (event.pageX - width) + 'px'
            }
          })
          .style('top', function () {
            if ((event.pageY - window.pageYOffset) + height - 30 > screenHeight) {
              return event.pageY - (height / 2) - (height / 4) + 'px'
            } else {
              return event.pageY - (height / 2) + (height / 4) + 'px'
            }
          })
      }
    }

    self.g
      .selectAll("path")
      .data(topojson.features)
      .enter()
      .append("path")
      .attr("class", this.geoType)
      .attr("stroke", "#666")
      .attr("d", self.path)
      .style("fill", function(d) {
        return self.getFillColor(d);
      })
      .style("opacity", 0.9)
      .style("stroke-width", 0.2)
      .on("click", function(d) {
        // self.highlightSelected(d.properties.pcon18nm, this);
        self.isZoomed = true;
        zoomTo(d);
      })
      .on("mouseover", function(event, d) {
        if (!mobile) {
          self.tooltip
            .text("")
            .style("display", "inline")
            .style("left", event.pageX - 100 + "px")
            .style("top", event.pageY - 150 + "px");
          self.getTooltip(d);
        }
        tooltipPos(event)
      })
      .on("mousemove", function(event) {
        tooltipPos(event)
      })
      .on("mouseout", function() {
        if (!mobile) {
          self.tooltip.style("display", "none");
        }
      });

    if (this.geoType !== "states") self.drawOverlay(self.states, "states");

    self.drawCities();
  }
};

const watch = {
  results() {
    this.drawMap();
  },
  showAll() {
    this.drawMap();
  },
  geoSelection() {
    this.drawMap();
  }
};

export default {
  props,
  mixins,
  computed,
  methods,
  watch,
  data() {
    return {
      tooltip: null,
      // config vars for map mixin
      height: {
        mobile: 520,
        desktop: 800
      }
    };
  },
  mounted() {
    window.addEventListener("resize", () => {
      setTimeout(() => {
        this.updateHeight();
        this.clearTooltip();
        this.drawMap();
      }, 500);
    });

    this.updateHeight();
    this.drawMap();
  },
  beforeUnmount() {
    this.clearTooltip();

    window.removeEventListener("resize", () => {
      setTimeout(() => {
        this.updateHeight();
        this.clearTooltip();
        this.drawMap();
      }, 500);
    });
  }
};
</script>

<style>
.results-map {
  overflow: hidden;
  width: 100%;
  height: 100%;
  cursor: pointer;
  margin-top: 20px;
}

.tooltip-topline-table {
  width: 100%;
}

.results-map-tooltip {
  position: absolute;
  text-align: center;
  width: auto;
  min-width: 260px;
  max-width: 320px;
  height: auto;
  padding: 10px 10px 15px;
  font: 12px sans-serif;
  background: white;
  border: 1px solid #222;
  pointer-events: none;
  z-index: 9999;
  cursor: pointer;
  border-radius: 4px;
}

.map-tooltip-content {
  width: 100%;
  height: auto;
}

.map-tooltip-top {
  width: 150px;
  height: 10px;
  bottom: 0;
  position: absolute;
  background: #fff;
}

.content-title {
  text-align: left;
  font-size: 14px;
  font-weight: 700;
  margin-bottom: 10px;
}

.candidates-table-headers th {
  font-weight: 400;
  font-size: 10px;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: rgba(0, 0, 0, 0.54);
  border-bottom: 1px solid #666;
  padding: 0 6px 3px;
  height: 32px;
}

.candidates-table-headers,
.candidates-table-row {
  height: 32px;
}

.candidates-table-row td {
  border-bottom: 1px solid #eeeeee;
  padding: 3px 6px;
}

.table-precincts {
  margin-top: 8px;
  text-align: left;
  font-size: 12px;
  opacity: 0.6;
}

path {
  stroke-linejoin: round;
  stroke-miterlimit: 2;
  stroke-linecap: round;
}

path:hover {
  fill-opacity: 0.85;
}

.state-labels {
  fill: #fff;
  fill-opacity: 0.9;
  font-weight: 700;
  text-anchor: middle;
  letter-spacing: 1px;
  font-size: 14px;
  text-transform: uppercase;
}

text.shadow {
  stroke: white;
  stroke-width: 3px;
  opacity: 0.8;
}

.city-label {
  font-family: 'Roboto Slab', serif;
  fill: black;
  fill-opacity: 1;
  font-weight: 500;
  letter-spacing: 0.3px;
}

circle,
text {
  pointer-events: none;
}

.flips-text {
  text-align: center;
  color: #979797;
  line-height: 30px;
}

@media (max-width: 720px) {
  path:hover {
    fill-opacity: 1;
  }
}
</style>
